/**
 * Activity Service
 * Handles all activity-related API calls
 */

import apiService from './api.service';
import { API_ENDPOINTS } from '../config/api.config';
import {
  Activity,
  ActivityListParams,
  ActivityStatsResponse,
  PaginatedResponse,
  CampaignTimelineResponse,
} from '../types/api.types';

const toPlainId = (value?: string): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue ? trimmedValue : undefined;
};

const buildActivityParams = (params?: ActivityListParams): Record<string, unknown> | undefined => {
  if (!params) {
    return undefined;
  }

  const {
    userId,
    campaignId,
    performed_by,
    campaign_id,
    ...rest
  } = params;

  const queryParams: Record<string, unknown> = { ...rest };
  const performedById = toPlainId(performed_by ?? userId);
  const campaignIdValue = toPlainId(campaign_id ?? campaignId);

  if (performedById) {
    queryParams.performed_by = performedById;
  }

  if (campaignIdValue) {
    queryParams.campaign_id = campaignIdValue;
  }

  return Object.keys(queryParams).length > 0 ? queryParams : undefined;
};

class ActivityService {
  /**
   * Get all activities with filtering
   */
  async getActivities(params?: ActivityListParams): Promise<PaginatedResponse<Activity>> {
    return await apiService.get<PaginatedResponse<Activity>>(
      API_ENDPOINTS.ACTIVITY.LIST,
      { params: buildActivityParams(params) }
    );
  }

  /**
   * Get recent activities
   */
  async getRecentActivities(limit = 10): Promise<Activity[]> {
    return await apiService.get<Activity[]>(
      API_ENDPOINTS.ACTIVITY.RECENT,
      { params: { limit } }
    );
  }

  /**
   * Get campaign timeline (activities related to a campaign)
   */
  async getCampaignTimeline(campaignId: string): Promise<Activity[]> {
    return await apiService.get<Activity[]>(
      API_ENDPOINTS.ACTIVITY.BY_CAMPAIGN(campaignId)
    );
  }

  /**
   * Get campaign timeline with detailed backend response shape
   */
  async getCampaignTimelineDetailed(campaignId: string): Promise<CampaignTimelineResponse> {
    return await apiService.get<CampaignTimelineResponse>(
      API_ENDPOINTS.ACTIVITY.BY_CAMPAIGN(campaignId)
    );
  }

  /**
   * Get user activities
   */
  async getUserActivities(userId: string, params?: ActivityListParams): Promise<PaginatedResponse<Activity>> {
    return await apiService.get<PaginatedResponse<Activity>>(
      API_ENDPOINTS.ACTIVITY.BY_USER(userId),
      { params: buildActivityParams(params) }
    );
  }

  /**
   * Get activity statistics
   */
  async getActivityStats(): Promise<ActivityStatsResponse> {
    return await apiService.get<ActivityStatsResponse>(API_ENDPOINTS.ACTIVITY.STATS);
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new ActivityService();
